export type RothkoPalette = {
  name: string
  painting: string
  hero: {
    ground: string
    topField: string[]
    bottomField: string[]
    haze: string[]
  }
  accents: {
    primary: string
    secondary: string
    warm: string
    deep: string
    cream: string
  }
}

export const palettes: Record<string, RothkoPalette> = {
  // ═══════════════════════════════════════════════════════════════════
  // HELEN FRANKENTHALER (Featured palettes first)
  // ═══════════════════════════════════════════════════════════════════

  // The Bay, 1963 — Deep saturated blue-green (DEFAULT)
  theBay: {
    name: 'The Bay',
    painting: 'Frankenthaler — The Bay, 1963',
    hero: {
      ground: '#1A2028',
      topField: ['#1E6878', '#2888A0', '#38A8B8', '#58C0C8'],
      bottomField: ['#2A5848', '#1E4838', '#143828'],
      haze: ['#38A8B8', '#284858', '#2A5848', '#1E4838'],
    },
    accents: {
      primary: '#2888A0',
      secondary: '#2A5848',
      warm: '#58C0C8',
      deep: '#1A4858',
      cream: '#D8F0F0',
    },
  },

  // Mountains and Sea, 1952 — Her breakthrough "soak-stain" painting
  mountainsAndSea: {
    name: 'Mountains and Sea',
    painting: 'Frankenthaler — Mountains and Sea, 1952',
    hero: {
      ground: '#F5F0E8',
      topField: ['#7BA3B8', '#9DBDC8', '#B8D4DC', '#D0E4E8'],
      bottomField: ['#D4A088', '#C4907A', '#B8806C'],
      haze: ['#B8D4DC', '#E0D4C8', '#D4A088', '#C4907A'],
    },
    accents: {
      primary: '#7BA3B8',
      secondary: '#D4A088',
      warm: '#E8C4A8',
      deep: '#5A8898',
      cream: '#F5F0E8',
    },
  },

  // Canyon, 1965 — Warm corals and earth tones
  canyon: {
    name: 'Canyon',
    painting: 'Frankenthaler — Canyon, 1965',
    hero: {
      ground: '#1E1816',
      topField: ['#D87858', '#E89878', '#F0B090', '#F5C8A8'],
      bottomField: ['#8B5A48', '#6B4238', '#4A2E28'],
      haze: ['#E89878', '#A86858', '#8B5A48', '#6B4238'],
    },
    accents: {
      primary: '#D87858',
      secondary: '#8B5A48',
      warm: '#F0B090',
      deep: '#4A2E28',
      cream: '#F8E8DC',
    },
  },

  // Nature Abhors a Vacuum, 1973 — Rich earth tones with yellow
  natureAbhors: {
    name: 'Nature Abhors',
    painting: 'Frankenthaler — Nature Abhors a Vacuum, 1973',
    hero: {
      ground: '#1C1810',
      topField: ['#C89830', '#D8B048', '#E8C860', '#F0D878'],
      bottomField: ['#8B6830', '#6B5028', '#4A3820'],
      haze: ['#D8B048', '#987840', '#8B6830', '#6B5028'],
    },
    accents: {
      primary: '#C89830',
      secondary: '#8B6830',
      warm: '#E8C860',
      deep: '#4A3820',
      cream: '#F8F0D0',
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // MARK ROTHKO
  // ═══════════════════════════════════════════════════════════════════

  // No. 61 (Rust and Blue), 1953
  rustAndBlue: {
    name: 'No. 61',
    painting: 'Rust and Blue, 1953',
    hero: {
      ground: '#1E1A17',
      topField: ['#8B3A2A', '#B85C38', '#C46B3F', '#D4956A'],
      bottomField: ['#2B4570', '#1A2D4A', '#162340'],
      haze: ['#D4956A', '#3A2A1E', '#1A2D4A', '#2B4570'],
    },
    accents: {
      primary: '#B85C38',
      secondary: '#2B4570',
      warm: '#D4956A',
      deep: '#8B3A2A',
      cream: '#E8D5B8',
    },
  },

  // No. 14 (Orange and Red on Red), 1961
  orangeOnRed: {
    name: 'No. 14',
    painting: 'Orange and Red on Red, 1961',
    hero: {
      ground: '#2A1210',
      topField: ['#D4622A', '#E87A3A', '#F09848', '#F2A85C'],
      bottomField: ['#9B2D1F', '#7A2218', '#5C1A12'],
      haze: ['#F09848', '#6B2A18', '#9B2D1F', '#7A2218'],
    },
    accents: {
      primary: '#D4622A',
      secondary: '#9B2D1F',
      warm: '#F09848',
      deep: '#5C1A12',
      cream: '#F5DCC4',
    },
  },

  // No. 6 (Violet, Green and Red), 1951
  violetGreenRed: {
    name: 'No. 6',
    painting: 'Violet, Green and Red, 1951',
    hero: {
      ground: '#1A1520',
      topField: ['#6B3A7A', '#8B4E9A', '#A468B0', '#B882C4'],
      bottomField: ['#3A6B48', '#2D5A3C', '#1E4A2E'],
      haze: ['#B882C4', '#4A3050', '#2D5A3C', '#3A6B48'],
    },
    accents: {
      primary: '#7B4490',
      secondary: '#3A6B48',
      warm: '#B882C4',
      deep: '#4A2060',
      cream: '#E8D5F0',
    },
  },

  // White Center (Yellow, Pink and Lavender on Rose), 1950
  whiteCenter: {
    name: 'White Center',
    painting: 'Yellow, Pink and Lavender on Rose, 1950',
    hero: {
      ground: '#1E1820',
      topField: ['#D4A832', '#E8C248', '#F0D060', '#F5DC78'],
      bottomField: ['#C4587A', '#A84468', '#8A3055'],
      haze: ['#F0D060', '#B06850', '#C4587A', '#A84468'],
    },
    accents: {
      primary: '#D4A832',
      secondary: '#A84468',
      warm: '#F0D060',
      deep: '#8A3055',
      cream: '#F8F0D8',
    },
  },

  // No. 3/No. 13 (Magenta, Black, Green on Orange), 1949
  magentaBlackGreen: {
    name: 'No. 3/No. 13',
    painting: 'Magenta, Black, Green on Orange, 1949',
    hero: {
      ground: '#1A1A18',
      topField: ['#B83060', '#D44878', '#E06090', '#E878A0'],
      bottomField: ['#2A5C3A', '#1E4A2E', '#143820'],
      haze: ['#E06090', '#4A2838', '#1E4A2E', '#2A5C3A'],
    },
    accents: {
      primary: '#C43868',
      secondary: '#2A5C3A',
      warm: '#E878A0',
      deep: '#8A2048',
      cream: '#F5D8E4',
    },
  },

  // Ochre and Red on Red, 1954
  ochreOnRed: {
    name: 'Ochre on Red',
    painting: 'Ochre and Red on Red, 1954',
    hero: {
      ground: '#201412',
      topField: ['#C49040', '#D4A450', '#E0B460', '#E8C070'],
      bottomField: ['#A03828', '#882C20', '#6C2018'],
      haze: ['#D4A450', '#7A3828', '#A03828', '#882C20'],
    },
    accents: {
      primary: '#C49040',
      secondary: '#A03828',
      warm: '#E0B460',
      deep: '#6C2018',
      cream: '#F0E0C8',
    },
  },

  // Blue and Grey, 1962
  blueAndGrey: {
    name: 'Blue and Grey',
    painting: 'Untitled (Blue and Grey), 1962',
    hero: {
      ground: '#141618',
      topField: ['#3868A0', '#4878B0', '#5888C0', '#6898D0'],
      bottomField: ['#6A6A6E', '#585858', '#484848'],
      haze: ['#6898D0', '#4A5868', '#585858', '#6A6A6E'],
    },
    accents: {
      primary: '#3868A0',
      secondary: '#6A6A6E',
      warm: '#6898D0',
      deep: '#1A3060',
      cream: '#D8E4F0',
    },
  },

}

export const DEFAULT_PALETTE = 'theBay'
